/**
 * <s-signup-popup> — Glitz List email popup
 * Opens its <dialog> once per visitor after a short delay (or when the mouse
 * heads for the browser bar on desktop). Dismissing hides it for
 * `snooze-days`; subscribing hides it for good. After the popup's own form
 * posts, the page reloads and the popup reopens to show the thank-you or error.
 *
 * Attributes:
 *   delay        — seconds before it opens (default 6)
 *   snooze-days  — days to stay hidden after "No thanks" / close (default 14)
 *   posted       — present when a customer form posted successfully on this load
 *   has-errors   — present when a customer form returned errors on this load
 */
const DISMISS_KEY = 'bg-signup-dismissed-at';
const JOINED_KEY = 'bg-signup-joined';
const SUBMIT_KEY = 'bg-signup-submitted';

class SSignupPopup extends HTMLElement {
  #dialog;
  #ac;
  #timer;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const submittedHere = sessionStorage.getItem(SUBMIT_KEY) === '1';
    if (submittedHere && (this.hasAttribute('posted') || this.hasAttribute('has-errors'))) {
      sessionStorage.removeItem(SUBMIT_KEY);
      if (this.hasAttribute('posted')) localStorage.setItem(JOINED_KEY, '1');
      this.#open();
    } else if (this.#shouldAutoOpen()) {
      const delay = Number(this.getAttribute('delay') || 6) * 1000;
      this.#timer = setTimeout(() => this.#autoOpen(), delay);
      document.documentElement.addEventListener('mouseleave', (e) => {
        if (e.clientY <= 0) this.#autoOpen();
      }, opts);
    }

    this.querySelector('form')?.addEventListener('submit', () => {
      sessionStorage.setItem(SUBMIT_KEY, '1');
    }, opts);

    // Backdrop click closes (clicks on the dialog box itself land on its children).
    this.#dialog.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      if (localStorage.getItem(JOINED_KEY) !== '1') {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    }, opts);

    this.querySelectorAll('[data-signup-close]').forEach((el) => {
      el.addEventListener('click', () => this.#dialog.close(), opts);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #shouldAutoOpen() {
    if (localStorage.getItem(JOINED_KEY) === '1') return false;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const snoozeMs = Number(this.getAttribute('snooze-days') || 14) * 86400000;
    return !dismissedAt || Date.now() - dismissedAt > snoozeMs;
  }

  // Timed / exit-intent opens re-check the snooze, so it never reappears after a dismiss.
  #autoOpen() {
    if (!this.#shouldAutoOpen()) return;
    this.#open();
  }

  #open() {
    clearTimeout(this.#timer);
    if (this.#dialog.open || document.querySelector('dialog[open]')) return;
    this.#dialog.showModal();
  }
}

if (!customElements.get('s-signup-popup')) {
  customElements.define('s-signup-popup', SSignupPopup);
}
